/* Firecamp.jsx — CR #325: Fire Prevention Play video submissions + voting (/#firecamp)
   Sites upload their performance to YouTube, Google Drive, or OneDrive, submit the link
   with their PPR district + site name, and the video embeds below for voting.
   Vote tallies are dashboard-only (Fire Voting tab) — the public page never
   shows counts. Votes dedup server-side per device; localStorage greys out
   the button after voting.

   What the page shows is driven entirely by FIRE_SEASON below — see the
   switch block for how to bring the whole thing back next year. */

// CR #338: JJ extended the district selector to cover all 12 PPR districts.
const FIRECAMP_DISTRICTS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
const FIRE_VOTES_LS_KEY = "ppr_fire_votes";

/* ═══════════════════════════════════════════════════════════════════════════
   THE SEASON SWITCH — this is the one line to change.

     "submissions"  Sites can submit. Shows the rules, the important dates,
                    the upload how-to, and the submit form.
     "voting"       Submissions closed. Videos are up with vote buttons.
     "archive"      Competition over. Results + videos to watch, nothing
                    interactive. ← where we are now.

   Running it again next year, in order:

     1. Update FIRE_SEASON_DATES below to next year's dates, then set
        FIRE_SEASON = "submissions".
     2. When the submission deadline passes → "voting".
     3. After the citywide show → "archive", then fill in the results:
        FIRE_DISTRICT_WINNERS, FIRE_CITY_PLACEMENTS, FIRE_CITY_AWARDS.

   Every section on the page keys off this, so nothing else needs touching.

   ONE THING THE SWITCH CANNOT DO: last year's videos live in the
   firecamp_videos table and will show up alongside the new ones. Clear or
   archive that table before reopening submissions, or district 2 will have
   two years of plays in it.
   ═══════════════════════════════════════════════════════════════════════════ */
const FIRE_SEASON = "archive";

const FIRE_SUBMISSIONS_OPEN = FIRE_SEASON === "submissions";
const FIRE_VOTING_OPEN      = FIRE_SEASON === "voting";
const FIRE_ARCHIVED         = FIRE_SEASON === "archive";

// Dates shown on the "Mark your calendar" cards and referenced in the copy.
// Kept here so next year is one edit in one place.
const FIRE_SEASON_DATES = {
  submitBy: "July 22",
  districtWinners: "July 24",
  citywide: "July 29",
  citywideVenue: "The Free Library, 1901 Vine Street",
};

/* District winners, announced July 24 — one play per district advances to the
   citywide competition. Recorded the short way J.J. sent them; siteKey below
   is what lets "Ramp" line up with the row submitted as "Ramp Playground". */
const FIRE_DISTRICT_WINNERS = {
  1: "Fox Chase",
  2: "Ramp",
  3: "Water Tower",
  4: "Simons",
  5: "Scanlon",
  6: "Heitzman",
  7: "West Mill Creek",
  8: "Mander",
  9: "Northern Liberties",
  10: "Eastwick",
  11: "Wharton Square",
  12: "Columbus Square",
};

/* Citywide results from the July 29 competition at the Free Library. Two
   tables on the sheets J.J. sent: the placements/major awards, and the
   category awards. Order here is the order they read on the page — placements
   top-down 1st to 3rd, which is the reverse of how the sheet lists them.
   Every citywide winner is also its district's winner, so rows carry the
   DISTRICT, not a name: the name is resolved from the submitted video rows at
   render time (see cityRows below) so these tables print the same full site
   name as the district chart and the starred video cards. `prefix` is for the
   Best Actor row, which names a performer at a site rather than the site. */
const FIRE_CITY_PLACEMENTS = [
  { label: "1st Place", district: 2 },
  { label: "2nd Place", district: 3 },
  { label: "3rd Place", district: 9 },
  { label: "Best Actor", district: 3, prefix: "Ethan from " },
  { label: "Ensemble Award", district: 9 },
  { label: "District Award", district: 10 },
];
const FIRE_CITY_AWARDS = [
  { label: "Joyce Craig Fire Safety Award", district: 4 },
  { label: "Excitement Award", district: 6 },
  { label: "Special Effects", district: 2 },
  { label: "Best Dance / Song", district: 12 },
  { label: "Creative Script", district: 3 },
  { label: "Fire Prevention Message", district: 5 },
];

// Words nearly every site name carries. Dropped before comparing so the
// distinguishing part of the name is what has to match.
const SITE_GENERIC_WORDS = new Set(["recreation", "rec", "center", "centre", "playground", "park"]);
const siteKey = (s) => String(s || "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, " ")
  .split(" ")
  .filter(w => w && !SITE_GENERIC_WORDS.has(w))
  .join(" ");
// Whole-word containment, not substring — otherwise "Ramp" would star a
// hypothetical "Ramparts Playground" too.
const isDistrictWinner = (v) => {
  const win = siteKey(FIRE_DISTRICT_WINNERS[Number(v.district)]);
  const site = siteKey(v.site_name);
  return !!win && !!site && ` ${site} `.includes(` ${win} `);
};
// CR #344: one vote per device per day. The stored record is stamped with the
// UTC day; a record from a prior day is ignored so the buttons re-enable.
// Matches the server, which folds the same UTC day into the dedup hash.
const fireVoteDay = () => new Date().toISOString().slice(0, 10);

function readFireVotes() {
  try {
    const o = JSON.parse(localStorage.getItem(FIRE_VOTES_LS_KEY) || "{}") || {};
    return (o.day === fireVoteDay() && o.voted) ? o.voted : {};
  } catch (e) { return {}; }
}

/* Drive facade: the raw /preview iframe is a bad player (tiny play target on
   mobile, burns the file's Drive view quota on every page load, occasional
   "Preview unavailable"). Instead show the video's Drive thumbnail with a big
   play button; the iframe only loads once someone taps. Falls back to a plain
   dark card if the thumbnail 404s (e.g. still processing). */
function DriveVideoCard({ videoId, siteName }) {
  const [playing, setPlaying] = React.useState(false);
  const [thumbOk, setThumbOk] = React.useState(true);
  if (playing) {
    return (
      <div style={{ position: "relative", paddingTop: "56.25%", background: "var(--c-ink)" }}>
        <iframe
          src={`https://drive.google.com/file/d/${videoId}/preview`}
          title={`${siteName} — Fire Prevention Play`}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        setPlaying(true);
        // The tap that swaps the facade for the iframe is the only play signal
        // this component gets -- what happens inside a Drive iframe is not
        // observable from here. See window.pprTrackMedia in analytics.js.
        if (window.pprTrackMedia) {
          window.pprTrackMedia({
            id: `drive:${videoId}`,
            kind: "video",
            title: `${siteName} — Fire Prevention Play`,
            collection: "fireplay",
          });
        }
      }}
      aria-label={`Play ${siteName} — Fire Prevention Play`}
      style={{ position: "relative", display: "block", width: "100%", paddingTop: "56.25%", background: "var(--c-ink)", border: 0, cursor: "pointer" }}
    >
      {thumbOk && (
        <img
          src={`https://drive.google.com/thumbnail?id=${videoId}&sz=w1000`}
          alt=""
          loading="lazy"
          onError={() => setThumbOk(false)}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}
      <span style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, color: "#f6f1ea", background: "rgba(0,0,0,0.35)" }}>
        <span style={{ width: 64, height: 64, borderRadius: "50%", background: "var(--c-primary)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, paddingLeft: 5 }}>▶</span>
        <span style={{ fontWeight: 600, fontSize: 14, textShadow: "0 1px 6px rgba(0,0,0,0.6)" }}>Tap to play</span>
      </span>
    </button>
  );
}

/* Two-column award table used by the citywide results block. Same visual
   language as the district winners chart below it — mono uppercase header,
   alternating rows — so the two read as one set of results. */
function FireAwardTable({ heading, rows, star }) {
  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 280 }}>
          <thead>
            <tr style={{ background: "var(--c-ink)", color: "var(--c-cream)" }}>
              <th scope="col" colSpan={2} style={{
                textAlign: "left", padding: "7px 14px",
                fontFamily: "var(--f-mono)", fontSize: 11.5, fontWeight: 700,
                letterSpacing: "0.1em", textTransform: "uppercase"
              }}>{heading}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.label} style={{
                background: i % 2 ? "var(--c-cream-deep)" : "var(--c-paper)",
                borderTop: i === 0 ? "none" : "1px solid var(--c-line)"
              }}>
                <th scope="row" style={{
                  textAlign: "left", padding: "7px 14px", verticalAlign: "top",
                  fontFamily: "var(--f-display)", fontSize: 14.5, fontWeight: 700
                }}>{row.label}</th>
                <td style={{ padding: "7px 14px", fontWeight: 600, textAlign: "right", verticalAlign: "top" }}>
                  {star && i === 0 && (
                    <span aria-hidden="true" style={{ color: "var(--c-accent-deep)", marginRight: 6 }}>★</span>
                  )}
                  {row.site}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FirecampPage() {
  const blank = { district: "", siteName: "", videoUrl: "" };
  const [data, setData] = React.useState(blank);
  const [submitted, setSubmitted] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [videos, setVideos] = React.useState([]);
  const [loadingVideos, setLoadingVideos] = React.useState(true);
  const [voted, setVoted] = React.useState(readFireVotes);
  const [expandedDistricts, setExpandedDistricts] = React.useState({});
  const update = (k, v) => setData(d => ({ ...d, [k]: v }));

  const toggleDistrict = (district) => {
    setExpandedDistricts(prev => ({
      ...prev,
      [district]: !prev[district]
    }));
  };


  // CR #348: warn (don't block) when the site name's first word already
  // appears among the live videos IN THE SAME DISTRICT. Catches the same
  // rec center / playground submitting a second video under a different link
  // — the server only dedups by video URL, so it can't see that case.
  const firstWord = (s) => String(s || "").trim().toLowerCase().split(/\s+/)[0] || "";
  const dupSiteName = React.useMemo(() => {
    const fw = firstWord(data.siteName);
    if (!fw || !data.district) return null;
    const hit = videos.find(v =>
      String(v.district) === String(data.district) && firstWord(v.site_name) === fw
    );
    return hit ? hit.site_name : null;
  }, [data.siteName, data.district, videos]);

  const loadVideos = React.useCallback(() => {
    fetch("/api/data/firecamp")
      .then(r => r.json())
      .then(j => setVideos(j.rows || []))
      .catch(() => {})
      .finally(() => setLoadingVideos(false));
  }, []);
  React.useEffect(() => { loadVideos(); }, [loadVideos]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/data/firecamp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "submit",
          district: Number(data.district),
          site_name: data.siteName,
          video_url: data.videoUrl,
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || `error_${res.status}`);
      setSubmitted({ ref: json.ref });
      loadVideos();
    } catch (err) {
      const code = String(err && err.message || "");
      setError(
        code === "already_submitted" ? "That video has already been submitted — it should be playing below."
        : code === "bad_video_url" ? "That link doesn't look like a YouTube, Google Drive, or OneDrive video link. Copy the share link straight from the app and try again."
        : code === "rate_limited" ? "Too many submissions from this connection — wait a few minutes and try again."
        : "Couldn't send right now. Please try again, or email Joseph.J.Pospiech@phila.gov."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onVote = async (videoId) => {
    if (voted[videoId]) return;
    // Optimistic: grey the button immediately; the server dedups per device
    // anyway, so a failed request just means the vote silently didn't count.
    const next = { ...voted, [videoId]: true };
    setVoted(next);
    try { localStorage.setItem(FIRE_VOTES_LS_KEY, JSON.stringify({ day: fireVoteDay(), voted: next })); } catch (e) {}
    try {
      await fetch("/api/data/firecamp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "vote", video_id: videoId })
      });
    } catch (e) {}
  };

  // Group live videos by district for the watch + vote section.
  const byDistrict = videos.reduce((acc, v) => {
    (acc[v.district] = acc[v.district] || []).push(v);
    return acc;
  }, {});
  const districtsWithVideos = Object.keys(byDistrict).map(Number).sort((a, b) => a - b);

  // Chart rows. Prefer the winner's full name as submitted so the chart and the
  // starred video card read identically; fall back to the short name while the
  // video list is still loading.
  const winnerRows = Object.keys(FIRE_DISTRICT_WINNERS)
    .map(Number)
    .sort((a, b) => a - b)
    .map(d => {
      const match = (byDistrict[d] || []).find(isDistrictWinner);
      return { district: d, siteName: match ? match.site_name : FIRE_DISTRICT_WINNERS[d] };
    });

  // Same resolution for the citywide tables: the full name as submitted
  // ("Ramp Playground"), falling back to the short name J.J. sent while the
  // video list is still loading.
  const cityRows = (rows) => rows.map(row => {
    const match = (byDistrict[row.district] || []).find(isDistrictWinner);
    const siteName = match ? match.site_name : FIRE_DISTRICT_WINNERS[row.district];
    return { ...row, site: `${row.prefix || ""}${siteName}` };
  });

  return (
    <div>
      <FormHero
        eyebrow="Fire Prevention Play"
        title={FIRE_ARCHIVED
          ? <>The plays are<br/>in the books.</>
          : <>Put your site's<br/>play on stage.</>}
        body={FIRE_ARCHIVED
          ? "Rec sites across Philadelphia wrote, staged and filmed their own Fire Prevention Play. Every performance is below, grouped by district, along with the district and citywide results. Watch them any time."
          : "Rec sites across Philadelphia are performing the Fire Prevention Play. Record your site's performance, upload it to YouTube, Google Drive, or OneDrive using the steps below, and submit the link. Your video appears on this page for the citywide vote."}
        bg="var(--c-primary)"
        logo={{ src: "/img/fire-dept-logo.png", alt: "Philadelphia Fire Department" }}
      />

      {/* VOTING CLOSED BANNER */}
      {FIRE_ARCHIVED && (
        <section className="section" style={{ background: "linear-gradient(135deg, #f6f1ea 0%, #f0e6d3 100%)", borderBottom: "3px solid var(--c-primary-deep)", paddingTop: 48, paddingBottom: 48 }}>
          <div className="container" style={{ maxWidth: 920 }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 20,
              padding: "24px 28px",
              background: "white",
              borderRadius: 16,
              boxShadow: "0 4px 20px rgba(216,85,43,0.12)",
              border: "2px solid var(--c-primary-deep)"
            }}>
              {/* Fire Logo */}
              <div style={{
                width: 72,
                height: 72,
                flexShrink: 0,
                borderRadius: 12,
                boxShadow: "0 4px 12px rgba(216,85,43,0.3)",
                overflow: "hidden"
              }}>
                <img src="/img/fire-logo.jpg" alt="Fire Prevention Play" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <div style={{ flex: 1 }}>
                <h2 style={{
                  fontSize: "clamp(22px, 3vw, 28px)",
                  fontWeight: 700,
                  margin: "0 0 8px 0",
                  color: "var(--c-primary-deep)",
                  fontFamily: "var(--f-display)"
                }}>
                  The competition is over — but the videos are here to stay.
                </h2>
                <p style={{
                  fontSize: 15,
                  lineHeight: 1.6,
                  margin: 0,
                  color: "var(--c-ink)"
                }}>
                  The citywide competition wrapped up on July 29 at the Free Library. Thank you to every rec center that took the stage, and to everyone who watched and voted. Every Fire Prevention Play performance is still below — watch and enjoy them any time.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* PHOTO SHORTCUT — the play photos live in the gallery, not here */}
      {FIRE_ARCHIVED && <FirePhotosCard />}

      {/* CITYWIDE WINNERS — July 29 competition at the Free Library */}
      {FIRE_ARCHIVED && (
        <section className="section" style={{ paddingTop: 44, paddingBottom: 12 }}>
          <div className="container" style={{ maxWidth: 920 }}>
            <div className="eyebrow">Citywide results</div>
            <h2 className="display" style={{ fontSize: "clamp(28px, 4.5vw, 44px)", margin: "10px 0 12px" }}>
              The citywide winners.
            </h2>
            <p style={{ fontSize: 14.5, color: "var(--c-ink-soft)", maxWidth: 620, lineHeight: 1.55, marginBottom: 24 }}>
              The 12 district winners performed at the citywide competition on July 29 at the Free Library,
              1901 Vine Street. Congratulations to every site that took the stage.
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 18 }}>
              <FireAwardTable heading="Placements" rows={cityRows(FIRE_CITY_PLACEMENTS)} star />
              <FireAwardTable heading="Awards" rows={cityRows(FIRE_CITY_AWARDS)} />
            </div>
          </div>
        </section>
      )}

      {/* DISTRICT WINNERS CHART */}
      {FIRE_ARCHIVED && (
        <section className="section" style={{ paddingTop: 24, paddingBottom: 44 }}>
          <div className="container" style={{ maxWidth: 920 }}>
            <div className="eyebrow">Results</div>
            <h2 className="display" style={{ fontSize: "clamp(28px, 4.5vw, 44px)", margin: "10px 0 12px" }}>
              The district winners.
            </h2>
            <p style={{ fontSize: 14.5, color: "var(--c-ink-soft)", maxWidth: 620, lineHeight: 1.55, marginBottom: 24 }}>
              One play from each of the 12 PPR districts advances to the citywide competition on July 29 at
              the Free Library, 1901 Vine Street. Each winning video is marked with a ★ in the district tabs below.
            </p>

            <div className="card" style={{ padding: 0, overflow: "hidden", maxWidth: 460 }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 300 }}>
                  <thead>
                    <tr style={{ background: "var(--c-ink)", color: "var(--c-cream)" }}>
                      <th scope="col" style={{
                        textAlign: "left", padding: "7px 14px", width: 92, whiteSpace: "nowrap",
                        fontFamily: "var(--f-mono)", fontSize: 11.5, fontWeight: 700,
                        letterSpacing: "0.1em", textTransform: "uppercase"
                      }}>District</th>
                      <th scope="col" style={{
                        textAlign: "left", padding: "7px 14px",
                        fontFamily: "var(--f-mono)", fontSize: 11.5, fontWeight: 700,
                        letterSpacing: "0.1em", textTransform: "uppercase"
                      }}>Winning site</th>
                    </tr>
                  </thead>
                  <tbody>
                    {winnerRows.map((row, i) => (
                      <tr key={row.district} style={{
                        background: i % 2 ? "var(--c-cream-deep)" : "var(--c-paper)",
                        borderTop: i === 0 ? "none" : "1px solid var(--c-line)"
                      }}>
                        <th scope="row" style={{
                          textAlign: "left", padding: "7px 14px", whiteSpace: "nowrap",
                          fontFamily: "var(--f-display)", fontSize: 15, fontWeight: 700
                        }}>{row.district}</th>
                        <td style={{ padding: "7px 14px", fontWeight: 600 }}>
                          <span aria-hidden="true" style={{ color: "var(--c-accent-deep)", marginRight: 8 }}>★</span>
                          {row.siteName}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* RULES + IMPORTANT DATES (CR #344) — competing-season only. Both are
          instructions for sites that still have a play to submit, so they come
          down in the archive and return with the switch. */}
      {!FIRE_ARCHIVED && (
      <section className="section" style={{ paddingTop: 8, paddingBottom: 40 }}>
        <div className="container" style={{ maxWidth: 920 }}>
          <div className="eyebrow">Before you submit</div>
          <h2 className="display" style={{ fontSize: "clamp(28px, 4.5vw, 44px)", margin: "10px 0 24px" }}>
            The rules.
          </h2>
          <div className="card">
            <ol style={{ paddingLeft: 22, fontSize: 14.5, lineHeight: 1.7, display: "flex", flexDirection: "column", gap: 12, margin: 0 }}>
              <li>The performers must be 12 years of age and under.</li>
              <li>There is no restriction to the number of participants.</li>
              <li>No actual fires may be used. All fires must be simulated! No combustibles are permitted.</li>
              <li>All scripts must be original and developed by this year's participants.</li>
              <li>Each play must be longer than two minutes, but not longer than six minutes. If a play is under two minutes, ten points will be deducted from their cumulative total. If a play is over six minutes, ten points will be deducted from the cumulative total.</li>
              <li>Only one full performance can be considered for judging.</li>
              <li>The best representative play from each district will be selected to compete at the citywide level. All videos will be displayed and submitted on phillyperforms.com.</li>
              <li>The decision of the judges is final.</li>
            </ol>
          </div>

          <div className="eyebrow" style={{ marginTop: 40 }}>Important dates</div>
          <h2 className="display" style={{ fontSize: "clamp(28px, 4.5vw, 44px)", margin: "10px 0 24px" }}>
            Mark your calendar.
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 18 }}>
            <div className="card">
              <div className="chip" style={{ marginBottom: 12 }}>Submit by</div>
              <div className="display" style={{ fontSize: 26, margin: "0 0 4px" }}>{FIRE_SEASON_DATES.submitBy}</div>
              <div style={{ fontSize: 14, color: "var(--c-ink-soft)" }}>All videos must be submitted.</div>
            </div>
            <div className="card">
              <div className="chip" style={{ marginBottom: 12 }}>District winners</div>
              <div className="display" style={{ fontSize: 26, margin: "0 0 4px" }}>{FIRE_SEASON_DATES.districtWinners}</div>
              <div style={{ fontSize: 14, color: "var(--c-ink-soft)" }}>One play selected per district.</div>
            </div>
            <div className="card">
              <div className="chip" style={{ marginBottom: 12 }}>Citywide competition</div>
              <div className="display" style={{ fontSize: 26, margin: "0 0 4px" }}>{FIRE_SEASON_DATES.citywide}</div>
              <div style={{ fontSize: 14, color: "var(--c-ink-soft)" }}>{FIRE_SEASON_DATES.citywideVenue}.</div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* WATCH (was WATCH + VOTE — vote controls retired with the competition) */}
      <section className="section" style={{ background: "var(--c-cream-deep)", borderTop: "2px solid var(--c-ink)" }}>
        <div className="container">
          <div className="eyebrow">{FIRE_VOTING_OPEN ? "Watch & vote" : "Watch"}</div>
          <h2 className="display" style={{ fontSize: "clamp(28px, 4.5vw, 44px)", margin: "10px 0 24px" }}>
            The performances, by district.
          </h2>
          <p style={{ fontSize: 14.5, color: "var(--c-ink-soft)", maxWidth: 640, lineHeight: 1.55, marginBottom: 28 }}>
            {!FIRE_VOTING_OPEN
              ? "Every performance submitted this year, grouped by district. Tap a district to watch. District winners are marked with a ★."
              : "One vote per video per device. Results are tallied by the Performing Arts Office at the end of the voting period."
            }
          </p>

          {loadingVideos ? (
            <p style={{ color: "var(--c-ink-soft)", fontSize: 14 }}>Loading videos…</p>
          ) : districtsWithVideos.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: 36 }}>
              <p style={{ fontSize: 15, color: "var(--c-ink-soft)", margin: 0 }}>
                {FIRE_SUBMISSIONS_OPEN
                  ? "No videos yet — yours could be the first. Submit below."
                  : "No videos to show right now."}
              </p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {districtsWithVideos.map(d => (
                <div key={d} style={{ border: "2px solid var(--c-ink)", borderRadius: 12, overflow: "hidden" }}>
                  <button
                    type="button"
                    onClick={() => toggleDistrict(d)}
                    aria-expanded={expandedDistricts[d] || false}
                    style={{
                      width: "100%",
                      padding: "16px 20px",
                      background: "var(--c-paper)",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                      transition: "background 120ms ease",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "var(--c-cream-deep)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "var(--c-paper)"; }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, textAlign: "left" }}>
                      <h3 className="display" style={{ fontSize: 22, margin: 0 }}>District {d}</h3>
                      <span className="chip">{byDistrict[d].length} {byDistrict[d].length === 1 ? "video" : "videos"}</span>
                    </div>
                    <span style={{ fontSize: 20, fontWeight: 700, transition: "transform 200ms ease", transform: expandedDistricts[d] ? "rotate(180deg)" : "rotate(0deg)" }}>
                      ▼
                    </span>
                  </button>

                  {expandedDistricts[d] && (
                    <div style={{ padding: "20px", borderTop: "2px solid var(--c-ink)", background: "var(--c-cream-soft)" }}>
                      <div className="fg-2" style={{ gap: 18 }}>
                        {byDistrict[d].map(v => {
                          const winner = isDistrictWinner(v);
                          return (
                          <div key={v.id} className="card" style={{ padding: 0, overflow: "hidden" }}>
                            {/* position:relative so the winner badge can sit over the player;
                                pointerEvents:none keeps it from eating taps on the play target. */}
                            <div style={{ position: "relative" }}>
                            {winner && (
                              <span style={{
                                position: "absolute", top: 10, left: 10, zIndex: 2, pointerEvents: "none",
                                padding: "5px 11px", borderRadius: "var(--r-pill)",
                                background: "var(--c-accent)", color: "var(--c-ink)",
                                fontFamily: "var(--f-mono)", fontSize: 10.5, fontWeight: 700,
                                letterSpacing: "0.08em", textTransform: "uppercase",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.35)"
                              }}>
                                ★ District winner
                              </span>
                            )}
                            {v.video_kind === "onedrive" ? (
                              <a
                                href={v.video_id}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={`${v.site_name} — Fire Prevention Play`}
                                // Counted alongside the Drive plays above. A
                                // OneDrive play leaves the site, so without
                                // this those sites would rank at zero and the
                                // whole list would read as a Drive-only chart.
                                onClick={() => {
                                  if (window.pprTrackMedia) {
                                    window.pprTrackMedia({
                                      id: `onedrive:${v.id}`,
                                      kind: "video",
                                      title: `${v.site_name} — Fire Prevention Play`,
                                      collection: "fireplay",
                                    });
                                  }
                                }}
                                style={{ position: "relative", display: "block", paddingTop: "56.25%", background: "var(--c-ink)", textDecoration: "none" }}
                              >
                                <span style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, color: "#f6f1ea", textAlign: "center", padding: 16 }}>
                                  <span style={{ fontSize: 40, lineHeight: 1 }}>▶</span>
                                  <span style={{ fontWeight: 600, fontSize: 15 }}>Watch on OneDrive</span>
                                  <span style={{ fontSize: 12.5, opacity: 0.75 }}>Opens in a new tab</span>
                                </span>
                              </a>
                            ) : v.video_kind === "drive" ? (
                              <DriveVideoCard videoId={v.video_id} siteName={v.site_name} />
                            ) : (
                              <div style={{ position: "relative", paddingTop: "56.25%", background: "var(--c-ink)" }}>
                                <iframe
                                  src={`https://www.youtube-nocookie.com/embed/${v.video_id}?rel=0&modestbranding=1`}
                                  title={`${v.site_name} — Fire Prevention Play`}
                                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
                                  loading="lazy"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              </div>
                            )}
                            </div>
                            <div style={{ padding: "16px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                              <div style={{ fontWeight: 600, fontSize: 15, display: "flex", alignItems: "center", gap: 6 }}>
                                {winner && <span aria-hidden="true" style={{ color: "var(--c-accent-deep)" }}>★</span>}
                                {v.site_name}
                              </div>
                              {/* No vote control outside the voting window — the greyed-out
                                  "Voting closed" button it used to render was one dead button
                                  per video, 73 of them, and said nothing the banner above
                                  doesn't already say. */}
                              {FIRE_VOTING_OPEN && (voted[v.id] ? (
                                <button className="btn secondary small" disabled style={{ opacity: 0.7, cursor: "default" }}>✓ Voted</button>
                              ) : (
                                <button className="btn small" onClick={() => onVote(v.id)}>Vote for this site</button>
                              ))}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* HOW TO UPLOAD — submissions-season only. Nobody is uploading during
          voting or in the archive, so it comes down with the form. */}
      {FIRE_SUBMISSIONS_OPEN && (
      <section className="section" style={{ paddingBottom: 0 }}>
        <div className="container" style={{ maxWidth: 920 }}>
          <div className="eyebrow">Step 1 — How to upload</div>
          <h2 className="display" style={{ fontSize: "clamp(28px, 4.5vw, 44px)", margin: "10px 0 24px" }}>
            From your phone, three easy ways.
          </h2>
          <div className="fg-3" style={{ gap: 18 }}>
            <div className="card">
              <div className="chip" style={{ marginBottom: 12 }}>Option A — YouTube</div>
              <ol style={{ paddingLeft: 20, fontSize: 14.5, lineHeight: 1.65, display: "flex", flexDirection: "column", gap: 8, margin: 0 }}>
                <li>Open the <strong>YouTube</strong> app and sign in.</li>
                <li>Tap the <strong>+</strong> button at the bottom, then <strong>Upload a video</strong>.</li>
                <li>Pick your recording and give it a title (your site name works great).</li>
                <li>Set visibility to <strong>Public</strong> or <strong>Unlisted</strong> — <em>not Private</em>, or it won't play here.</li>
                <li>Once it finishes uploading, tap <strong>Share → Copy link</strong>.</li>
              </ol>
            </div>
            <div className="card">
              <div className="chip" style={{ marginBottom: 12 }}>Option B — Google Drive</div>
              <ol style={{ paddingLeft: 20, fontSize: 14.5, lineHeight: 1.65, display: "flex", flexDirection: "column", gap: 8, margin: 0 }}>
                <li>Open the <strong>Google Drive</strong> app.</li>
                <li>Tap <strong>+ New → Upload</strong> and pick your recording.</li>
                <li>After it uploads, tap the <strong>⋮</strong> menu next to the file, then <strong>Share</strong>.</li>
                <li>Under General access, choose <strong>"Anyone with the link"</strong> — otherwise it won't play here.</li>
                <li>Tap <strong>Copy link</strong>.</li>
              </ol>
            </div>
            <div className="card">
              <div className="chip" style={{ marginBottom: 12 }}>Option C — OneDrive</div>
              <ol style={{ paddingLeft: 20, fontSize: 14.5, lineHeight: 1.65, display: "flex", flexDirection: "column", gap: 8, margin: 0 }}>
                <li>Open the <strong>OneDrive</strong> app and sign in.</li>
                <li>Tap <strong>+ → Upload</strong> and pick your recording.</li>
                <li>After it uploads, tap the <strong>⋯</strong> menu next to the file, then <strong>Share</strong>.</li>
                <li>Set the link to <strong>"Anyone with the link can view"</strong> — otherwise it won't open here.</li>
                <li>Tap <strong>Copy link</strong>.</li>
              </ol>
            </div>
          </div>
          <p style={{ fontSize: 13.5, color: "var(--c-ink-soft)", marginTop: 14 }}>
            Questions? Email{" "}
            <a href="mailto:Joseph.J.Pospiech@phila.gov">Joseph.J.Pospiech@phila.gov</a>.
          </p>
        </div>
      </section>
      )}

      {/* SUBMIT FORM — submissions season only.
          Restored from 60f0f9e1^ (it had been deleted outright when submissions
          closed, which meant no flag could bring it back). The handler, the
          field state and the CR #348 duplicate-site warning had all survived in
          this file the whole time; only the markup was missing. */}
      {FIRE_SUBMISSIONS_OPEN && (
      <section className="section">
        <div className="container" style={{ maxWidth: 920 }}>
          <div className="eyebrow">Step 2 — Submit your link</div>
          <h2 className="display" style={{ fontSize: "clamp(28px, 4.5vw, 44px)", margin: "10px 0 24px" }}>
            Enter your site for the vote.
          </h2>
          {submitted ? (
            <div className="card" style={{ textAlign: "center", padding: 36 }}>
              <div style={{ fontSize: 48, marginBottom: 10 }}>✦</div>
              <h3 className="display" style={{ fontSize: 28, marginBottom: 10 }}>Got it — your video is in.</h3>
              <p style={{ fontSize: 15, color: "var(--c-ink-soft)", lineHeight: 1.55, maxWidth: 520, margin: "0 auto 20px" }}>
                It's live in the voting section below. Reference: {submitted.ref}. Share this page with
                your families so they can vote for your site.
              </p>
              <button className="btn secondary" onClick={() => { setSubmitted(null); setData(blank); }}>
                Submit another video
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="card" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Row>
                <Field
                  label="Your PPR district"
                  type="select"
                  options={[""].concat(FIRECAMP_DISTRICTS)}
                  optionLabels={["—"].concat(FIRECAMP_DISTRICTS.map(d => `District ${d}`))}
                  value={data.district}
                  onChange={v => update("district", v)}
                  required
                />
                <Field label="Site name" value={data.siteName} onChange={v => update("siteName", v)} required placeholder="e.g. Athletic Recreation Center" />
              </Row>
              {dupSiteName && (
                <p role="status" style={{ margin: 0, padding: "10px 12px", borderRadius: 8, fontSize: 13.5, lineHeight: 1.5, color: "#7a5c00", background: "#fff7d6", border: "1px solid #f0d97a" }}>
                  Heads up — <strong>{dupSiteName}</strong> is already posted in District {data.district} below.
                  If that's this same site, this would be a duplicate. Only submit if it's a different site.
                </p>
              )}
              <Field
                label="Video link (YouTube, Google Drive, or OneDrive)"
                value={data.videoUrl}
                onChange={v => update("videoUrl", v)}
                required
                placeholder="Paste your YouTube, Google Drive, or OneDrive link"
              />
              {error && <p style={{ color: "var(--c-primary-deep)", fontSize: 14, margin: 0 }}>{error}</p>}
              <div>
                <button type="submit" className="btn" disabled={submitting || !data.district || !data.siteName.trim() || !data.videoUrl.trim()}>
                  {submitting ? "Submitting…" : "Submit video"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
      )}

      {/* Contact line. The upload how-to carried this, and that section is
          submissions-only — without this the voting season had no way to reach
          anyone on the whole page.
          Deliberately NOT shown in archive season: J.J. asked for the "Questions
          about next year's Fire Prevention Play?" line to come off once the
          season closed. So archive shows no contact line here at all. Bring it
          back by dropping the !FIRE_ARCHIVED guard. */}
      {!FIRE_SUBMISSIONS_OPEN && !FIRE_ARCHIVED && (
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container" style={{ maxWidth: 920 }}>
            <p style={{ fontSize: 13.5, color: "var(--c-ink-soft)", margin: 0 }}>
              Questions? Email <a href="mailto:Joseph.J.Pospiech@phila.gov">Joseph.J.Pospiech@phila.gov</a>.
            </p>
          </div>
        </section>
      )}
    </div>
  );
}

/* Shortcut to the 114 competition photos, which live in the gallery's `firesafety`
   set rather than on this page -- one photo archive, one lightbox, one place to
   maintain. The deep link is the ?set= shape GalleryPage's catFromHash reads;
   changing the cat id in GALLERY_CATS breaks this href and it will fail SILENTLY
   (unknown set falls back to "all"), so change both together.
   Gated on FIRE_ARCHIVED like every other section here: reopening submissions
   next year hides the 2026 photos. If they should outlive the switch, that is a
   deliberate edit, not an oversight. */
function FirePhotosCard() {
  const shots = [
    { src: "/img/fire-play-001.jpg", alt: "Performers and Philadelphia firefighters on stage at the citywide Fire Prevention Play competition" },
    { src: "/img/fire-play-095.jpg", alt: "Northern Liberties Recreation Center campers in fire helmets during their play" },
    { src: "/img/fire-play-040.jpg", alt: "Two young performers mid-scene at the Free Library" },
  ];
  return (
    <section className="section" style={{ paddingTop: 40, paddingBottom: 8 }}>
      <div className="container" style={{ maxWidth: 920 }}>
        <a
          href="#gallery?set=firesafety"
          className="fire-photos-card"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            alignItems: "center",
            gap: 24,
            padding: 24,
            border: "2px solid var(--c-ink)",
            borderRadius: 18,
            background: "var(--c-paper)",
            color: "var(--c-ink)",
            textDecoration: "none",
            boxShadow: "var(--shadow-lift)",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div className="eyebrow">Photos</div>
            <div className="display" style={{ fontSize: "clamp(22px, 3vw, 30px)", marginTop: 6, lineHeight: 1.1 }}>
              See the plays in photos.
            </div>
            <p style={{ fontSize: 14.5, color: "var(--c-ink-soft)", marginTop: 10, lineHeight: 1.55, maxWidth: 460 }}>
              114 photos from the citywide competition on July 29 at the Free
              Library, shot by Joe O&rsquo;Connor.
            </p>
            <span className="btn small ochre" style={{ marginTop: 16, display: "inline-flex" }}>
              Open the photo gallery &rarr;
            </span>
          </div>
          <div className="fire-photos-thumbs" style={{ display: "flex", gap: 8 }}>
            {shots.map(s => (
              <img
                key={s.src}
                src={s.src}
                alt={s.alt}
                loading="lazy"
                style={{
                  width: 104, height: 132, objectFit: "cover", display: "block",
                  border: "2px solid var(--c-ink)", borderRadius: 12,
                }}
              />
            ))}
          </div>
        </a>
        <style>{`
          .fire-photos-card:hover { transform: translate(-3px, -3px); }
          .fire-photos-card { transition: transform 150ms; }
          @media (prefers-reduced-motion: reduce) {
            .fire-photos-card { transition: none; }
            .fire-photos-card:hover { transform: none; }
          }
          @media (max-width: 720px) {
            .fire-photos-card { grid-template-columns: 1fr !important; }
            /* min-width:0 is load-bearing, not tidying (CR #377). A flex item's
               default min-width:auto floors an image at its transferred size --
               height 96px through the photo's own aspect ratio, i.e. ~142px --
               so width:100% could never shrink it. Three of those plus the gaps
               wanted 442px inside a 298px card, and the third thumb hung outside
               the border and put the whole page into horizontal scroll. */
            .fire-photos-thumbs img { width: 100% !important; height: 96px !important; min-width: 0; }
            .fire-photos-thumbs { width: 100%; }
          }
        `}</style>
      </div>
    </section>
  );
}

Object.assign(window, { FirecampPage, FirePhotosCard });
